import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { Box, Button, Text } from '@factorialco/deprecated-design-system'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'

import { usePolicyPeriods } from 'modules/payroll_bundle/lib/policyPeriodsContext'

interface Props {
  employeesWithPendingPaymentsCount: number
  onStartPaymentsGeneration: () => void
}

const EmptyState = ({ employeesWithPendingPaymentsCount, onStartPaymentsGeneration }: Props) => {
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const {
    access: { locale: userLocale },
  } = useCurrentSession()
  const { policyId, currentPeriod } = usePolicyPeriods()
  const policyPeriodId = currentPeriod?.id

  const [src, setSrc] = useState<string>('')

  const getImageSrc = (locale: string) =>
    `/assets/images/payroll/payments-empty-state-v2/${locale}.png`

  const imageSrc = useMemo(() => getImageSrc(userLocale), [userLocale])
  const defaultSrc = getImageSrc('en')

  useEffect(() => {
    setSrc(imageSrc)
  }, [imageSrc])

  const onError = () => setSrc(defaultSrc)

  const hasPendingPayments = employeesWithPendingPaymentsCount > 0

  const labels = useMemo(
    () => ({
      title: hasPendingPayments
        ? i18n.t('payroll_bundle.results_payments.payments.empty_state.has_results.title')
        : i18n.t('payroll_bundle.results_payments.payments.empty_state.no_results.title'),
      description: hasPendingPayments
        ? i18n.t('payroll_bundle.results_payments.payments.empty_state.has_results.description', {
            count: employeesWithPendingPaymentsCount,
          })
        : i18n.t('payroll_bundle.results_payments.payments.empty_state.no_results.description'),
      submitButton: hasPendingPayments
        ? i18n.t('payroll_bundle.results_payments.payments.empty_state.has_results.submit')
        : i18n.t('payroll_bundle.results_payments.payments.empty_state.no_results.submit'),
    }),
    [hasPendingPayments, employeesWithPendingPaymentsCount]
  )

  const handleSubmit = hasPendingPayments
    ? onStartPaymentsGeneration
    : () =>
        navigate(
          navigation.payrollBundle.policy.period.cycles.results.fullPath({
            policyId: String(policyId),
            periodId: String(policyPeriodId),
          })
        )

  return (
    <Box width="full" alignItems="center" gap="s64" paddingTop="s48">
      <Box alignItems="center" textAlign="center" width="s400" gap="s16">
        <Text size="300" weight="semibold" color="grey1000">
          {labels.title}
        </Text>
        <Text size="200" weight="normal" color="grey800">
          {labels.description}
        </Text>
        <Button type="primary" onClick={handleSubmit}>
          {labels.submitButton}
        </Button>
      </Box>
      <Box height="fit-content" alignItems="center" justifyContent="center" flexGrow>
        <img src={src} width="100%" onError={onError} alt={labels.title} />
      </Box>
    </Box>
  )
}

export default EmptyState
